'use client';

import { useEffect } from 'react';
import useSWR from 'swr';
import { shallow } from 'zustand/shallow';

import { getOrderDataClient } from 'src/data/AccountApi/getOrderData.client';
import { getDocumentRequests } from 'src/data/DocumentUploadApi/DocumentUploadApi';
import { isApprovalRequiringAction } from 'src/data/DocumentUploadApi/DocumentUploadApi.types';
import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { authStore } from 'src/stores/authStore';
import { documentStore } from 'src/stores/documentStore';

const DOCUMENT_REQUESTS_SWR_KEY = 'document-requests';

/**
 * Hook to fetch document requests (with SWR dedup) and update the document store.
 * Only fetches if user is authenticated.
 * Also fetches order and product data for the first pending document request via SWR.
 */
export const useDocumentRequests = (enabled: boolean = true) => {
  const { userData } = authStore((state) => ({ userData: state.userData }), shallow);
  const { setDocumentRequestsLoading, setDocumentRequests, setDocumentOrderData, clearDocumentData } = documentStore(
    (state) => ({
      setDocumentRequestsLoading: state.setDocumentRequestsLoading,
      setDocumentRequests: state.setDocumentRequests,
      setDocumentOrderData: state.setDocumentOrderData,
      clearDocumentData: state.clearDocumentData,
    }),
    shallow,
  );

  const {
    data: requests,
    error,
    isLoading,
  } = useSWR(userData && enabled ? DOCUMENT_REQUESTS_SWR_KEY : null, getDocumentRequests, { revalidateOnFocus: false });

  const firstOrderId = requests
    ?.find((request) =>
      request.customer_document_approvals?.some((approval) => isApprovalRequiringAction(approval.status)),
    )
    ?.external_references?.find((ref) => ref.external_type === 'order')?.external_id;

  const { data: orderData } = useSWR(
    firstOrderId && enabled ? ['order', firstOrderId] : null,
    firstOrderId && enabled ? () => getOrderDataClient(firstOrderId) : null,
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (!userData || !enabled) {
      clearDocumentData();
      return;
    }
    if (isLoading) {
      setDocumentRequestsLoading();
      return;
    }
    if (error) {
      setDocumentRequests([]);
      return;
    }
    if (requests) {
      setDocumentRequests(requests);
    }
  }, [
    userData,
    enabled,
    isLoading,
    error,
    requests,
    clearDocumentData,
    setDocumentRequestsLoading,
    setDocumentRequests,
  ]);

  useEffect(() => {
    if (!orderData || !userData || !enabled) return;
    const { orderData: order, productData } = orderData;
    const carData = productData ? productDetailPayloadToBaseCarData(productData) : null;
    setDocumentOrderData(order, carData);
  }, [orderData, userData, enabled, setDocumentOrderData]);
};
